import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { Link, ChevronRight, Delete } from 'wix-ui-icons-common';
import ConnectionDropdown from '../connection-dropdown';
import greenVIcon from '../../../assets/Green V.svg';
import redErrorIcon from '../../../assets/Red Error.svg';
import styles from './styles.scss';

import { Text, Button, IconButton, Tooltip } from 'wix-style-react';

class Connection extends Component {
  static propTypes = {
    connectionUID: PropTypes.string,
    srcItems: PropTypes.array,
    dstItems: PropTypes.array,
    initialSrcId: PropTypes.string,
    initialDstId: PropTypes.string,
    disabled: PropTypes.bool,
    onConnect: PropTypes.func,
    onDelete: PropTypes.func,
    showSeperator: PropTypes.bool,
    type: PropTypes.oneOf(['new', 'existing', 'invalid']),
    t: PropTypes.func,
    mailchimp: PropTypes.object,
    biLogger: PropTypes.object,
  };

  static defaultProps = {
    type: 'new',
    showSeperator: false,
    srcItems: [],
    dstItems: [],
    disabled: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      srcId: this.props.initialSrcId,
      dstId: this.props.initialDstId,
    };
  }

  handleTooltipShow(biData) {
    const { biLogger } = this.props;
    biLogger.mcIntegrationModalTooltipView(biData);
  }

  handleTooltipHide(biData) {
    const { biLogger } = this.props;
    biLogger.mcIntegrationModalTooltipClose(biData);
  }

  render() {
    const { srcId, dstId } = this.state;
    const { type, t, showSeperator, biLogger } = this.props;

    const disabled = type !== 'new';

    const ConnectingIcon = ({ type: _type }) =>
      _type === 'new' ? (
        <div className={styles.connectingIcon}>
          <span className={styles.arrowRight}>
            <ChevronRight size="32px" />
          </span>
        </div>
      ) : (
        <div className={styles.connectingIcon}>
          <div className={styles.link}>
            <Link />
          </div>
        </div>
      );

    const NewConnectionActions = ({
      onConnect,
      srcId: _srcId,
      dstId: _dstId,
    }) => (
      <div className={styles.connectButton}>
        <Button
          size="small"
          dataHook="connect-button"
          onClick={() => onConnect(_srcId, _dstId)}
          disabled={!(_srcId && _dstId)}
        >
          {t(`mailchimp.connection.connect`)}
        </Button>
      </div>
    );

    const ExistingConnectionActions = ({ type: _type, onDelete }) => {
      const renderInvalidConnection = () => {
        const tooltipName = 'connection not active';
        return (
          <Tooltip
            textAlign="start"
            placement="left"
            moveBy={{ x: -2, y: -1 }}
            content={
              <Text weight="normal" size="small">
                {t(`mailchimp.invalid.hover.content`)}
              </Text>
            }
            onShow={() => this.handleTooltipShow({ tooltipName })}
            onHide={() => this.handleTooltipHide({ tooltipName })}
          >
            <span className={styles.error}>
              <img src={redErrorIcon} alt="" />
            </span>
          </Tooltip>
        );
      };

      const renderDeleteConnection = () => {
        const tooltipName = 'delete connection warning';
        return (
          <Tooltip
            textAlign="start"
            placement="left"
            moveBy={{ x: 20, y: 0 }}
            content={
              <Text weight="normal" size="small">
                {t(`mailchimp.delete.hover.content`)}
              </Text>
            }
            onShow={() => this.handleTooltipShow({ tooltipName })}
            onHide={() => this.handleTooltipHide({ tooltipName })}
          >
            <div className={styles.trash}>
              <IconButton
                size="small"
                skin="light"
                onClick={onDelete}
                dataHook="delete-connection-button"
              >
                <Delete size="20px" />
              </IconButton>
            </div>
          </Tooltip>
        );
      };
      return (
        <div
          data-hook="existing-connection-container"
          className={styles.existingConnectionContainer}
        >
          {_type === 'existing' && (
            <span>
              <img src={greenVIcon} alt="" />
            </span>
          )}
          {_type === 'invalid' && renderInvalidConnection()}
          {renderDeleteConnection()}
        </div>
      );
    };

    const optionName = (items, optionID) => {
      const item = items.find((i) => i.id === optionID);
      if (item) {
        return item.text;
      }

      return null;
    };

    return (
      <div>
        <div className={styles.connectionsStrip}>
          <div className={styles.subscribersSelection}>
            <div>
              <ConnectionDropdown
                selectedId={srcId}
                onSelect={(selectedId) => {
                  biLogger.mcIntegrationWixLabelSelect({ selectedId });
                  this.setState({ srcId: selectedId });
                }}
                dataHook="mi-wix-label-dropdown"
                items={this.props.srcItems}
                disabled={disabled}
                disabledPlaceholder={optionName(
                  this.props.srcItems,
                  this.props.initialSrcId,
                )}
                placeholder={t(`mailchimp.choose.label.title`)}
              />
            </div>
          </div>

          <ConnectingIcon type={type} />

          <div className={styles.subscribersSelection}>
            <div>
              <ConnectionDropdown
                selectedId={dstId}
                onSelect={(selectedId) => {
                  biLogger.mcIntegrationMcListSelect({ selectedId });
                  this.setState({ dstId: selectedId });
                }}
                dataHook="mi-mailchimp-list-dropdown"
                items={this.props.dstItems}
                disabled={disabled}
                disabledPlaceholder={
                  type === 'invalid'
                    ? t('mailchimp.list.doesnt.exist')
                    : optionName(this.props.dstItems, this.props.initialDstId)
                }
                placeholder={t(`mailchimp.choose.list.title`)}
              />
            </div>
          </div>

          {type === 'new' ? (
            <NewConnectionActions
              onConnect={this.props.onConnect}
              srcId={srcId}
              dstId={dstId}
            />
          ) : (
            <ExistingConnectionActions
              onDelete={() => this.props.onDelete(this.props.connectionUID)}
              type={type}
            />
          )}
        </div>

        {showSeperator && <div className={styles.seperatorLine} />}
      </div>
    );
  }
}

export default translate()(
  inject('mailchimp', 'biLogger')(observer(Connection)),
);
